import { useEffect } from 'react';

import { useMachine, useSelector } from '@xstate/react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import type { SnapshotFrom } from 'xstate';
import { transition } from 'xstate';

import { useKnownAvailableParams, useNavigate } from '@change-corgi/core/react/router';

import { useStartAPetitionPageData } from '../../pageData';
import { createLogic } from '../logic';
import { assertNavigationEvent } from '../utils';

import { useInitialDraft } from './useInitialDraft';
import { useSubscribeToMachine } from './useSubscribeToMachine';

type StateValue<MACHINE> = SnapshotFrom<MACHINE>['value'];

export function useStartAPetitionState() {
	const navigate = useNavigate();
	const navigationType = useNavigationType();
	const { pathname, search } = useLocation();
	const pathnameWithoutStep = pathname.split('/').slice(0, -1).join('/');
	const { step } = useKnownAvailableParams<{ step: string }>();
	const pageData = useStartAPetitionPageData();
	const isSap3NavigationExperiment = pageData.data.experiments.starfishSap3Navigation.variation === 'variant';

	const initialDraft = useInitialDraft(pageData.data.draft);

	const machine = createLogic();
	const [snapshot, send, actorRef] = useMachine(machine, {
		input: { draft: initialDraft },
		/**
		 * Handles navigation events for all `submit` events to ensure the URL
		 * is synchronized with the state machine
		 */
		inspect: (event) => {
			try {
				assertNavigationEvent(event);
				const navigationEvent = event.event;
				const currentSnapshot = actorRef.getSnapshot();
				const [nextSnapshot] = transition(machine, currentSnapshot, navigationEvent);
				if (typeof nextSnapshot.value === 'string' && nextSnapshot.value !== 'idle') {
					const fullPath = `${pathnameWithoutStep}/${nextSnapshot.value}${search}`;
					navigate(fullPath, { replace: isSap3NavigationExperiment });
				}
			} catch {
				// do nothing if the event is not a navigation event
			}
		},
	});

	// subscribes to the state machine and updates the draft in local storage or in-memory on every change
	useSubscribeToMachine(actorRef);

	const draft = useSelector(actorRef, (state) => state.context.draft);

	const matches = (value: StateValue<typeof machine>) => snapshot.matches(value);

	/**
	 * Handles synchronizing the state machine with the URL
	 * when the user navigates using the browser's back/forward buttons
	 */
	useEffect(() => {
		// initializes the current step of the machine based on the URL
		// this approach is necessary so we don't initialize the machine with a "first" step
		// then immediately transition to the URL based step
		if (snapshot.value === 'idle') {
			send({ type: 'sync', step });
			return;
		}

		// if the user navigates using the browser's back/forward buttons,
		// we need to ensure the state machine is synchronizes the machine with the URL
		if (navigationType === NavigationType.Pop || snapshot.value !== step) {
			send({ type: 'sync', step });
		}
	}, [navigationType, send, snapshot.value, step]);

	return { actions: { send, matches }, data: { currentStep: snapshot.value, draft } };
}
